import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { normalizeString } from "../utils/string";
import { getBoundsFromGeometry } from "../utils/getBoundsFromGeometry";

// Helper para encapsular a busca de todos os dados do Backend
const fetchAllData = async () => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const API_URL = process.env.REACT_APP_API_URL;
    const authHeaders = { headers: { Authorization: `Bearer ${API_KEY}` } };

    // Removi 'nucleosRes' da lista para bater com o número de requests no Promise.all
    const [
        munRes, 
        terrRes, 
 
        progestaoRes
    ] = await Promise.all([
        axios.get(`${API_URL}/dados/dados_separados/municipios`, authHeaders),
        axios.get(`${API_URL}/dados/dados_separados/territorios`, authHeaders),
        axios.get(`${API_URL}/dados/imoveis-progestao`, authHeaders)
    ]);

    // Unifica as features garantindo que não quebre se .features ou .municipios vierem vazios
    const rawFeatures = [
        ...(munRes.data.municipios || munRes.data.features || []),
        ...(terrRes.data.territorios || terrRes.data.features || [])
        // Se decidir voltar núcleos futuramente, adicione aqui
    ];

    const features = rawFeatures.map(f => ({
        ...f,
        properties: {
            ...f.properties,
            municipio_normalizado: normalizeString(f.properties.MUNICIPIO || f.properties.NM_MUN || ""),
            nucleo_normalizado: normalizeString(f.properties.NUCLEO || f.properties.NOME_AREA || ""),
            territorio_normalizado: normalizeString(f.properties.TERRITORIO || f.properties.NM_TERRITORIO || "")
        }
    }));

    return { 
        features, 
        progestaoData: progestaoRes.data.data || [] 
    };
};

export function useNucleoFilters(selectedTerritorio, selectedMunicipio, selectedNucleo, selectedUnidade,  activeView) {
    const [allData, setAllData] = useState({ 
        features: [], 
        estatisticasPlanilha: {},
        progestaoData: []
    });
    
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Efeito de carga inicial
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const data = await fetchAllData();
                setAllData(data);
                setError(null);
            } catch (err) {
                console.error("Erro ao carregar dados:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const { features, progestaoData, estatisticasPlanilha } = allData;

    // --- 1. Mapeamentos e Opções de Seleção (Dinamismo dos Filtros) ---

    const municipioToTerritorioMap = useMemo(() => {
        const map = {};
        features.forEach(f => {
            const m = f.properties.municipio_normalizado;
            const t = f.properties.territorio_normalizado;
            if (m && t && !map[m]) map[m] = t;
        });
        return map;
    }, [features]);

    const territoriosOptions = useMemo(() => {
        const unique = new Set(features.map(f => f.properties.TERRITORIO).filter(Boolean));
        return ["Todos", ...Array.from(unique).sort()];
    }, [features]);

    const municipiosFiltrados = useMemo(() => {
        // 1. Pega os dados do Pró-Gestão (onde estão as unidades administrativas)
        const dataArr = Array.isArray(progestaoData) ? progestaoData : [];
        
        const tNorm = normalizeString(selectedTerritorio);
        const isTAll = selectedTerritorio === "Todos";

        // 2. Filtra os dados pelo Território selecionado
        const filtradosPorTerritorio = dataArr.filter(item => {
            const itemT = normalizeString(item.territorio || "");
            return isTAll || itemT === tNorm;
        });

        // 3. Extrai apenas os nomes dos municípios que aparecem nesses dados
        const uniqueMunicipios = new Set(
            filtradosPorTerritorio
                .map(item => item.municipio || item.localizacao)
                .filter(Boolean)
        );

        // 4. Retorna a lista ordenada com o "Todos"
        return ["Todos", ...Array.from(uniqueMunicipios).sort()];
    }, [progestaoData, selectedTerritorio]);


    // Opções de Unidade Administrativa baseada no Município/Território
    const unidadesOptions = useMemo(() => {
        const dataArr = Array.isArray(progestaoData) ? progestaoData : [];
        const tNorm = normalizeString(selectedTerritorio);
        const mNorm = normalizeString(selectedMunicipio);
        
        const filtered = dataArr.filter(item => {
            const itemT = normalizeString(item.territorio || "");
            const itemM = normalizeString(item.municipio || item.localizacao || "");
            return (selectedTerritorio === "Todos" || itemT === tNorm) && 
                   (selectedMunicipio === "Todos" || itemM === mNorm);
        });

        const unique = new Set(filtered.map(i => i.unidade || i.tipo_unidade).filter(Boolean));
        return ["Todos", ...Array.from(unique).sort()];
    }, [progestaoData, selectedTerritorio, selectedMunicipio]);
    


    // --- 2. Lógica de Filtragem de Dados ---

    const filteredData = useMemo(() => {
        const tNorm = normalizeString(selectedTerritorio);
        const mNorm = normalizeString(selectedMunicipio);
        const nNorm = normalizeString(selectedNucleo);
        const uNorm = normalizeString(selectedUnidade);
        const isTAll = selectedTerritorio === "Todos";
        const isMAll = selectedMunicipio === "Todos";
        const isNAll = selectedNucleo === "Todos";
        const isUAll = selectedUnidade === "Todos";

        // Filtro Pró-Gestão
        const progestaoFiltrados = (Array.isArray(progestaoData) ? progestaoData : []).filter(item => {
            const itemT = normalizeString(item.territorio || "");
            const itemM = normalizeString(item.municipio || item.localizacao || "");
            const itemB = normalizeString(item.bairro || "");
            const itemU = normalizeString(item.unidade || item.tipo_unidade || "");

            return (isTAll || itemT === tNorm) && 
                   (isMAll || itemM === mNorm) && 
                   (isUAll || itemU === uNorm) && 
                   (isNAll || itemB === nNorm);
        });

        // Filtro GeoJSON (Mapa)
        const filteredFeatures = features.filter(f => {
            const itemM = f.properties.municipio_normalizado;
            const itemT = municipioToTerritorioMap[itemM] || f.properties.territorio_normalizado;
            
            const matchT = isTAll || itemT === tNorm;
            const matchM = isMAll || itemM === mNorm;
            
            return matchT && matchM; // Agora a função sempre retorna true ou false
        });


        return { 
            filteredFeatures, 
            progestaoFiltrados 
        };
    }, [features, progestaoData, selectedTerritorio, selectedMunicipio, selectedNucleo, selectedUnidade, municipioToTerritorioMap]);

    const { progestaoFiltrados } = filteredData;
    // --- 3. Cálculos de Resumo (Cards e Gráficos) ---

    const resumoProgestao = useMemo(() => {
        return progestaoFiltrados.reduce((acc, item) => {
            acc.total_imoveis += 1;
            // Converte string de área (ex: "1.200,50") para número float tratável
            const areaStr = String(item.area_construida || "0").replace(/\./g, '').replace(',', '.');
            const area = parseFloat(areaStr);
            acc.area_total_construida += isNaN(area) ? 0 : area;
            return acc;
        }, { total_imoveis: 0, area_total_construida: 0 });
    }, [progestaoFiltrados]);





    const municipiosMap = useMemo(() => {
        let municipios = features.filter(f => f.properties.TIPO?.toLowerCase() === "municipio");
        const territorioNorm = normalizeString(selectedTerritorio);
        const municipioNorm = normalizeString(selectedMunicipio);

        // Filtra por território se um território for selecionado
        if (selectedTerritorio !== "Todos") {
            municipios = municipios.filter(f => f.properties.territorio_normalizado === territorioNorm);
        }

        // Filtra por município se um município específico for selecionado
        if (selectedMunicipio !== "Todos") {
            municipios = municipios.filter(f => f.properties.municipio_normalizado === municipioNorm);
        }

        return municipios;
    }, [features, selectedTerritorio, selectedMunicipio]);


    const territorios = useMemo(() => {
        const allTerritorios = features.filter(f => f.properties.TIPO?.toLowerCase() === "territorio");

        if (selectedTerritorio === "Todos") {
            return allTerritorios;
        }

        const territorioNorm = normalizeString(selectedTerritorio);
        return allTerritorios.filter(f => f.properties.territorio_normalizado === territorioNorm);
    }, [features, selectedTerritorio]); // Adiciona dependência de filtro
        // --- 4. Camadas e Bounds do Mapa ---

    const filteredBounds = useMemo(() => {
        if (loading) return null;

        const tNorm = normalizeString(selectedTerritorio);
        const mNorm = normalizeString(selectedMunicipio);


        // 1. PRIORIDADE MÁXIMA: Unidade Administrativa (Pontos do Pró-Gestão)
        if (selectedUnidade !== "Todos" && progestaoFiltrados.length > 0) {
            let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
            let hasCoords = false;

            progestaoFiltrados.forEach(item => {
                const lat = parseFloat(String(item.latitude).replace(',', '.'));
                const lng = parseFloat(String(item.longitude).replace(',', '.'));
                if (!isNaN(lat) && !isNaN(lng)) {
                    minLat = Math.min(minLat, lat);
                    maxLat = Math.max(maxLat, lat);
                    minLng = Math.min(minLng, lng);
                    maxLng = Math.max(maxLng, lng);
                    hasCoords = true;
                }
            });

            if (hasCoords) {
                if (minLat === maxLat) return [[minLat - 0.005, minLng - 0.005], [maxLat + 0.005, maxLng + 0.005]];
                return [[minLat, minLng], [maxLat, maxLng]];
            }
        }

        // 2. SEGUNDA PRIORIDADE: Município Selecionado (Polígono)
        if (selectedMunicipio !== "Todos") {
            const munFeature = features.find(f => 
                f.properties.TIPO === "MUNICIPIO" && 
                normalizeString(f.properties.MUNICIPIO) === mNorm
            );
            if (munFeature) return getBoundsFromGeometry(munFeature.geometry);
        }

        // 3. TERCEIRA PRIORIDADE: Território Selecionado (Fit no polígono do território)
        if (selectedTerritorio !== "Todos") {
            const terrFeature = features.find(f => 
                f.properties.TIPO === "TERRITORIO" && 
                normalizeString(f.properties.TERRITORIO) === tNorm
            );
            if (terrFeature) return getBoundsFromGeometry(terrFeature.geometry);
        }

        // 4. FALLBACK: Todas as feições visíveis (Geral do Estado)
        if (features.length > 0) {
            return features.reduce((acc, f) => {
                const b = getBoundsFromGeometry(f.geometry);
                if (!b) return acc;
                if (!acc) return b;
                return [
                    [Math.min(acc[0][0], b[0][0]), Math.min(acc[0][1], b[0][1])],
                    [Math.max(acc[1][0], b[1][0]), Math.max(acc[1][1], b[1][1])]
                ];
            }, null);
        }

        return null;
    }, [selectedTerritorio, selectedMunicipio, selectedUnidade, progestaoFiltrados, features, loading]);


    return {
        loading,
        error,
        features,
        progestaoData: progestaoFiltrados,
        resumoProgestao,
        territoriosOptions,
        municipiosFiltrados,
        unidadesOptions,

        municipiosMap,
        territorios,

        filteredBounds,

        estatisticasPlanilha
    };
}